import { api } from "@/lib/api-client";
import type { Paginated } from "../agents/api";

/** Derived account state from the backend — the single thing the UI renders. */
export type BillingState =
  | "active"
  | "trialing"
  | "past_due"
  | "cancelled"
  | "none";

export type Subscription = {
  state: BillingState;
  plan_name?: string | null;
  plan_slug?: string | null;
  billing_interval?: string;
  current_period_end?: string | null;
  cancel_at_period_end?: boolean;
  trial_days_remaining?: number | null;
  /** Stripe customer exists — the billing portal will work. */
  has_billing_account?: boolean;
  /** On the Chawowa two-rate model — no self-serve plan picking. */
  platform_billed?: boolean;
  /** Real Stripe subscription live (monthly-in-advance billing running). */
};

export type Plan = {
  id: string;
  name: string;
  slug: string;
  description?: string;
  price_monthly_cents: number;
  price_annual_cents: number;
  features?: Record<string, unknown>;
  limits?: Record<string, number>;
};

export type Invoice = {
  id: string;
  status: string;
  amount_due_cents: number;
  period_start: string;
  pdf_url?: string | null;
  hosted_invoice_url?: string | null;
};

export type QuotaItem = {
  used: number;
  limit: number;
  extra?: number;
  effective?: number;
};
export type QuotaData = Record<string, QuotaItem>;

export type SavedCard = {
  saved: boolean;
  brand?: string;
  last4?: string;
  exp_month?: number | null;
  exp_year?: number | null;
};

export type AutoTopUp = {
  enabled: boolean;
  threshold_cents?: number;
  pack_id?: string | null;
  has_card?: boolean;
};

export type PlatformUsage = {
  enabled: boolean;
  plan?: { id: string; name: string };
  period_start?: string;
  rates?: { per_customer_cents: number; per_minute_cents: number };
  customers?: { count: number; fee_cents: number };
  minutes?: { used: number; fee_cents: number };
  total_cents?: number;
};

export type CreditPack = {
  id: string;
  name: string;
  amount_cents: number;
  currency: string;
};

export type CreditTransaction = {
  id: string;
  kind: "topup" | "usage" | "adjustment";
  amount_cents: number;
  balance_after_cents: number;
  description: string;
  created_at: string;
};

export type CreditHistoryType = "topup" | "fee" | "call" | "adjustment";

export type CreditHistoryPage = {
  results: CreditTransaction[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
};

export type CreditWallet = {
  enabled: boolean;
  balance_cents?: number;
  per_minute_cents?: number;
  low_threshold_cents?: number;
  transactions?: CreditTransaction[];
};

export const billingApi = {
  platformUsage: async () => {
    const res = await api.get<PlatformUsage>("/billing/platform-usage/");
    return res.data;
  },
  plans: async () => {
    const res = await api.get<Paginated<Plan> | Plan[]>("/billing/plans/");
    return Array.isArray(res.data) ? res.data : res.data.results;
  },
  invoices: async () => {
    const res = await api.get<Paginated<Invoice> | Invoice[]>(
      "/billing/invoices/",
    );
    return Array.isArray(res.data) ? res.data : res.data.results;
  },
  subscription: async () => {
    const res = await api.get<Subscription | null>("/billing/subscription/");
    return res.data;
  },
  quota: async () => {
    const res = await api.get<QuotaData | null>("/billing/quota/");
    return res.data;
  },
  checkout: async (data: {
    plan_id: string;
    billing_interval?: "monthly" | "annual";
  }) => {
    const res = await api.post<{ checkout_url: string }>(
      "/billing/checkout/",
      data,
    );
    return res.data;
  },
  portal: async () => {
    const res = await api.post<{ portal_url: string }>("/billing/portal/", {});
    return res.data;
  },
  credit: async () => {
    const res = await api.get<CreditWallet>("/billing/credit/");
    return res.data;
  },
  creditTransactions: async (params: {
    page: number;
    type?: CreditHistoryType;
  }) => {
    const res = await api.get<CreditHistoryPage>(
      "/billing/credit/transactions/",
      { params: { page: params.page, page_size: 25, type: params.type } },
    );
    return res.data;
  },
  creditPacks: async () => {
    const res = await api.get<Paginated<CreditPack> | CreditPack[]>(
      "/billing/credit-packs/",
    );
    return Array.isArray(res.data) ? res.data : res.data.results;
  },
  creditCheckout: async (data: { pack_id: string }) => {
    const res = await api.post<{ checkout_url: string }>(
      "/billing/credit/checkout/",
      data,
    );
    return res.data;
  },
  // Buy a pack with the card already on file — no Stripe redirect.
  creditQuickTopUp: async (data: { pack_id: string }) => {
    const res = await api.post<{ ok: boolean; balance_cents: number }>(
      "/billing/credit/quick-topup/",
      data,
    );
    return res.data;
  },
  paymentMethod: async () => {
    const res = await api.get<SavedCard>("/billing/payment-method/");
    return res.data;
  },
  savePaymentMethod: async () => {
    const res = await api.post<{ checkout_url: string }>(
      "/billing/payment-method/",
      {},
    );
    return res.data;
  },
  removePaymentMethod: async () => {
    await api.delete("/billing/payment-method/");
  },
  autoTopUp: async () => {
    const res = await api.get<AutoTopUp>("/billing/auto-topup/");
    return res.data;
  },
  saveAutoTopUp: async (data: {
    enabled: boolean;
    threshold_cents: number;
    pack_id: string | null;
  }) => {
    const res = await api.put<AutoTopUp>("/billing/auto-topup/", data);
    return res.data;
  },
};
